import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { useToast } from '../utils/ToastContext'
import { trpc } from '../utils/trpc'

export interface EmailVariable {
	key: string
	label: string
	description: string
	example: string
	required?: boolean
}

export interface TemplateFormData {
	name: string
	subject: string
	body: string
	tags: string[]
}

interface UseEmailTemplateEditorConfig {
	tag: string
	organizationId: string
	variables: EmailVariable[]
	selectedTemplateId: string | null | undefined
	onTemplateCreated?: (templateId: string) => void
}

export function useEmailTemplateEditor(config: UseEmailTemplateEditorConfig) {
	const { tag, organizationId, variables, selectedTemplateId, onTemplateCreated } = config
	const toast = useToast()
	const queryClient = useQueryClient()

	const [isTemplateEditorOpen, setIsTemplateEditorOpen] = useState(false)
	const [editingTemplateId, setEditingTemplateId] = useState<string | undefined>(undefined)
	const [templateForm, setTemplateForm] = useState<TemplateFormData>({
		name: '',
		subject: '',
		body: '',
		tags: [tag],
	})
	const [templateErrors, setTemplateErrors] = useState<Partial<Record<keyof TemplateFormData, string>>>({})

	// Fetch email templates
	const { data: templates, isLoading: isLoadingTemplates } = useQuery({
		...trpc.client.emailTemplates.search.queryOptions({
			organizationId,
			tag,
			page: 1,
			perPage: 100,
		}),
		enabled: !!organizationId,
	})

	// Fetch selected template details
	const { data: selectedTemplate, isLoading: isLoadingTemplate } = useQuery({
		...trpc.client.emailTemplates.getById.queryOptions({
			id: selectedTemplateId || '',
			organizationId: organizationId || '',
		}),
		enabled: !!selectedTemplateId && !!organizationId,
	})

	// Fetch template being edited
	const { data: editingTemplate } = useQuery({
		...trpc.client.emailTemplates.getById.queryOptions({
			id: editingTemplateId || '',
			organizationId: organizationId || '',
		}),
		enabled: !!editingTemplateId && !!organizationId,
	})

	// Create template mutation
	const { mutate: createTemplate, isPending: isCreating } = useMutation({
		...trpc.client.emailTemplates.create.mutationOptions({
			onSuccess: (data) => {
				toast.showToast('Email template created successfully')
				invalidateTemplates()
				onTemplateCreated?.(data[0].id)
				closeTemplateEditor()
			},
			onError: (error) => {
				toast.showToast(`Failed to create template: ${error.message}`, 'error')
			},
		}),
	})

	// Edit template mutation
	const { mutate: editTemplate, isPending: isEditingTemplate } = useMutation({
		...trpc.client.emailTemplates.edit.mutationOptions({
			onSuccess: () => {
				toast.showToast('Email template updated successfully')
				invalidateTemplates()
				if (editingTemplateId) {
					queryClient.invalidateQueries({
						queryKey: trpc.client.emailTemplates.getById.queryKey({
							id: editingTemplateId,
							organizationId,
						}),
					})
				}
				closeTemplateEditor()
			},
			onError: (error) => {
				toast.showToast(`Failed to update template: ${error.message}`, 'error')
			},
		}),
	})

	const invalidateTemplates = () => {
		queryClient.invalidateQueries({
			queryKey: trpc.client.emailTemplates.search.queryKey({
				organizationId,
			}),
			exact: false,
			refetchType: 'all',
		})
	}

	// Load template data into editor when editing
	useEffect(() => {
		if (editingTemplate && editingTemplateId) {
			setTemplateForm({
				name: editingTemplate.name,
				subject: editingTemplate.subject,
				body: editingTemplate.body,
				tags: editingTemplate.tags ?? [tag],
			})
		}
	}, [editingTemplate, editingTemplateId, tag])

	const openCreateEditor = () => {
		setEditingTemplateId(undefined)
		setTemplateForm({ name: '', subject: '', body: '', tags: [tag] })
		setTemplateErrors({})
		setIsTemplateEditorOpen(true)
	}

	const openEditEditor = () => {
		setEditingTemplateId(selectedTemplateId || undefined)
		setTemplateForm({
			name: selectedTemplate?.name || '',
			subject: selectedTemplate?.subject || '',
			body: selectedTemplate?.body || '',
			tags: selectedTemplate?.tags ?? [tag],
		})
		setTemplateErrors({})
		setIsTemplateEditorOpen(true)
	}

	const closeTemplateEditor = () => {
		setIsTemplateEditorOpen(false)
		setEditingTemplateId(undefined)
		setTemplateForm({ name: '', subject: '', body: '', tags: [tag] })
		setTemplateErrors({})
	}

	const validateTemplateForm = (): boolean => {
		const newErrors: Partial<Record<keyof TemplateFormData, string>> = {}
		if (!templateForm.name.trim()) newErrors.name = 'Template name is required'
		if (!templateForm.subject.trim()) newErrors.subject = 'Subject is required'
		if (!templateForm.body.trim()) newErrors.body = 'Body is required'
		setTemplateErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const handleSaveTemplate = () => {
		if (!validateTemplateForm()) return

		if (editingTemplateId) {
			editTemplate({
				id: editingTemplateId,
				organizationId,
				name: templateForm.name,
				subject: templateForm.subject,
				body: templateForm.body,
				tags: templateForm.tags,
			})
		} else {
			createTemplate({
				organizationId,
				name: templateForm.name,
				subject: templateForm.subject,
				body: templateForm.body,
				tags: [tag],
			})
		}
	}

	const getPreview = () => {
		const source = isTemplateEditorOpen ? templateForm : selectedTemplate
		if (!source || (!source.subject && !source.body)) return null

		let subject = source.subject || ''
		let body = source.body || ''

		variables.forEach((variable) => {
			const escapedKey = variable.key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
			const regex = new RegExp(escapedKey, 'gi')
			subject = subject.replace(regex, variable.example)
			body = body.replace(regex, variable.example)
		})

		return { subject, body }
	}

	const handleCopyVariable = async (variable: string) => {
		try {
			await navigator.clipboard.writeText(variable)
			toast.showToast(`Copied ${variable} to clipboard`)
		} catch {
			toast.showToast(`Variable: ${variable}`, 'info')
		}
	}

	const templateOptions = (templates?.data || []).map((t) => ({
		value: t.id,
		label: t.name,
	}))

	const isTemplateSaving = isCreating || isEditingTemplate

	return {
		// State
		isTemplateEditorOpen,
		editingTemplateId,
		templateForm,
		setTemplateForm,
		templateErrors,
		// Query data
		templates,
		selectedTemplate,
		isLoadingTemplates,
		isLoadingTemplate,
		templateOptions,
		// Actions
		openCreateEditor,
		openEditEditor,
		closeTemplateEditor,
		handleSaveTemplate,
		handleCopyVariable,
		getPreview,
		// Status
		isTemplateSaving,
	}
}
